import React, { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Range } from '@tiptap/core';
import { Editor, posToDOMRect } from '@tiptap/react';

import { useSelector } from 'react-redux';
import { PopupPosition } from '../../tiptapPopup';
import { dangerouslyUseTiptapActiveEditor, useActiveTiptapEditorCallback } from '../../store/tiptapEditorStoreHooks';
import BoardLinkPopup from './BoardLinkPopup';
import { getCurrentlyEditingId } from '../../../../element/selection/currentlyEditingSelector';
import { activePopupSelector } from '../../../popupPanel/popupOpenSelector';
import { UseBoardLinkManagerResult } from './useBoardLinkManager';
import { defer } from 'lodash';
import { BoardLinkSearchItem } from './boardLinkTypes';

type UseBoardLinkPopupManagerResult = {
    openPopup: () => void;
    closePopup: () => void;
    popupElement: ReactNode;
    isPopupOpen: boolean;
};

const getBoardLinkPopupPosition = (editor: Editor, range: Range): PopupPosition => {
    const rangeRect = posToDOMRect(editor.view, range.from, range.from);

    return {
        anchor: 'bottom',
        anchorRect: rangeRect,
        point: {
            x: rangeRect.x,
            y: rangeRect.y + rangeRect.height,
        },
    };
};

export const useBoardLinkPopupManager = ({
    prepareBoardLinkSelection,
    handleBoardSelect,
    handlePastedBoardLink,
}: UseBoardLinkManagerResult): UseBoardLinkPopupManagerResult => {
    const [position, setPosition] = useState<PopupPosition | null>(null);
    const [isPopupOpen, setIsPopupOpen] = useState<boolean>(false);

    const currentlyEditingId = useSelector(getCurrentlyEditingId);
    const activePopups = useSelector(activePopupSelector);
    const activeEditor = dangerouslyUseTiptapActiveEditor();

    const openedForElementIdRef = useRef<string | null>(null);

    const openPopup = useActiveTiptapEditorCallback(
        (editor) => {
            if (!editor) return;

            const { from, to } = editor.state.selection;

            prepareBoardLinkSelection();
            openedForElementIdRef.current = currentlyEditingId;
            setPosition(getBoardLinkPopupPosition(editor, { from, to }));
            setIsPopupOpen(true);
        },
        [prepareBoardLinkSelection, currentlyEditingId],
    );

    const closePopup = useCallback(() => {
        setIsPopupOpen(false);
        setPosition(null);
    }, []);

    const handleSelect = useCallback(
        (item: BoardLinkSearchItem) => {
            const didInsert = handleBoardSelect(item);
            if (!didInsert) return false;

            closePopup();
            return true;
        },
        [handleBoardSelect, closePopup],
    );

    const handlePasteBoardLink = useCallback(
        (text: string) => {
            const didInsert = handlePastedBoardLink(text);
            if (!didInsert) return false;

            closePopup();
            return true;
        },
        [handlePastedBoardLink, closePopup],
    );

    // Close the popup if the editing element changes
    useEffect(() => {
        if (!isPopupOpen) return;

        const openedForElementId = openedForElementIdRef.current;
        if (!openedForElementId) return;

        if (!currentlyEditingId || currentlyEditingId !== openedForElementId) closePopup();
    }, [isPopupOpen, currentlyEditingId, closePopup]);

    // Close the popup if another popup is opened
    useEffect(() => {
        if (!isPopupOpen || !activePopups?.size) return;

        const otherPopupIsOpen = activePopups.keySeq().size > 0;
        if (!otherPopupIsOpen) return;

        const timeoutId = defer(() => {
            closePopup();
        });

        return () => clearTimeout(timeoutId);
    }, [isPopupOpen, activePopups, closePopup]);

    // Close the popup if the edited element regains focus or receives a keydown event
    useEffect(() => {
        if (!isPopupOpen || !activeEditor) return;

        const handleKeydownEvent = () => {
            closePopup();
        };

        activeEditor.on('focus', closePopup);
        activeEditor.view.dom.addEventListener('keydown', handleKeydownEvent, true);

        return () => {
            activeEditor.off('focus', closePopup);
            activeEditor.view.dom.removeEventListener('keydown', handleKeydownEvent, true);
        };
    }, [isPopupOpen, activeEditor, closePopup]);

    const boardLinkPopup = useMemo(() => {
        if (!isPopupOpen || !position) return null;

        return (
            <BoardLinkPopup
                isOpen={isPopupOpen}
                position={position}
                onSelect={handleSelect}
                onPasteBoardLink={handlePasteBoardLink}
            />
        );
    }, [isPopupOpen, position, handleSelect, handlePasteBoardLink]);

    return {
        openPopup,
        closePopup,
        popupElement: boardLinkPopup,
        isPopupOpen,
    };
};
